import React, { useState } from "react";
import { object, string } from "yup";

import { closeAllHashModals, openNewBusinessModal } from "lib/navigation";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import { Form, Formik } from "formik";
import Grid from "@material-ui/core/Grid";
import { Button, SecondaryButton } from "components/Form";
import { Label, useSubmitHandler } from "components/Form/FormikControls";
import { Row } from "components/Layout";
import { GlobalBusinessAutoCompletePicker } from "components/Form/AutoCompleteBusinessPicker";
import { ExternalLink } from "components/Link";
import { SmallText } from "components/Text";
import { BusinessAction } from "actions";
import { useDispatch } from "react-redux";

interface GlobalBusinessSearchFormProps {
  handleClose: () => void;
}

const GlobalBusinessSearchForm = ({
  handleClose,
}: GlobalBusinessSearchFormProps) => {
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  useSubmitHandler(false, setIsSubmitEnabled);

  const handleAddNewBusiness = () => {
    // Return back to this screen - note that it will lose search terms.
    openNewBusinessModal(window.location.hash);
  };

  return (
    <>
      <DialogContent dividers>
        <Grid container spacing={2} justifyContent="space-around">
          <Grid item xs={12} md={6}>
            <Label tooltip="Adding a Connected Business gives you instant access to data already in AgriNous and enables quicker and easier workflows and communications.">
              Search for a Business
            </Label>
            <SmallText>
              Try typing a Business&apos; name, PIC, email or ABN. You can
              continue to update information on these Businesses as you do
              currently.
            </SmallText>
            <GlobalBusinessAutoCompletePicker name="globalBusinessId" />
            <Row marginVertical={2} baseline>
              Can&apos;t find a connected Business?&nbsp;
              <ExternalLink onClick={handleAddNewBusiness} color="primary">
                Create a new Business
              </ExternalLink>
              &nbsp;instead.
            </Row>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <SecondaryButton data-tour="cancel" onClick={handleClose} type="button">
          Cancel
        </SecondaryButton>
        <Button type="submit" disabled={!isSubmitEnabled}>
          Add
        </Button>
      </DialogActions>
    </>
  );
};

const validationSchema = object().shape({
  globalBusinessId: string().nullable().required("Business is required."),
});

interface GlobalBusinessCreateModalProps {
  handleClose: () => void;
}

interface SubmitValuesProps {
  globalBusinessId?: string;
}

const GlobalBusinessCreateModal = ({
  handleClose,
}: GlobalBusinessCreateModalProps) => {
  const dispatch = useDispatch();

  const handleSubmit = (values: SubmitValuesProps) => {
    dispatch(BusinessAction.getOrCreateGlobalBusiness(values.globalBusinessId));
    closeAllHashModals();
  };

  return (
    <Dialog open maxWidth="md" fullWidth onClose={handleClose}>
      <DialogTitle onClose={handleClose}>Add Connected Business</DialogTitle>
      <Formik
        initialValues={{}}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form data-tour="globalBusinessSearchForm">
          <GlobalBusinessSearchForm handleClose={handleClose} />
        </Form>
      </Formik>
    </Dialog>
  );
};

export default GlobalBusinessCreateModal;
